/**
 * Framework-agnostic scheduling engine behind `useVisibilityAwarePoll`. Kept separate from the React
 * hook (and its timers/DOM dependencies made injectable) so the pause/resume/no-overlap behavior can
 * be unit tested with a fake clock, without a DOM or React renderer.
 */

export interface VisibilityAwarePollerDeps {
  readonly setTimeout: (handler: () => void, ms: number) => number;
  readonly clearTimeout: (id: number) => void;
  readonly isHidden: () => boolean;
  readonly addVisibilityListener: (listener: () => void) => void;
  readonly removeVisibilityListener: (listener: () => void) => void;
}

export interface VisibilityAwarePollerOptions {
  /**
   * When true, keep polling on the same cadence even while the tab is hidden. Use this only for
   * polls whose execution is coupled to being polled (e.g. Gemini image/video generation, which
   * advances server-side the moment its transaction status is fetched) - pausing those would
   * silently stall an in-progress generation whenever the user backgrounds the tab.
   */
  readonly criticalWhileHidden?: boolean;
  /** Fire the first tick right away instead of waiting a full `intervalMs` after starting. */
  readonly runImmediately?: boolean;
}

export function defaultVisibilityAwarePollerDeps(): VisibilityAwarePollerDeps {
  return {
    setTimeout: (handler, ms) => globalThis.setTimeout(handler, ms) as unknown as number,
    clearTimeout: (id) => globalThis.clearTimeout(id as unknown as ReturnType<typeof globalThis.setTimeout>),
    isHidden: () => typeof document !== "undefined" && document.visibilityState === "hidden",
    addVisibilityListener: (listener) => {
      if (typeof document !== "undefined") {
        document.addEventListener("visibilitychange", listener);
      }
    },
    removeVisibilityListener: (listener) => {
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", listener);
      }
    }
  };
}

/**
 * Starts a self-correcting poll loop: the next tick is only scheduled once the previous callback
 * settles (so a slow callback can't cause overlapping runs), ticks are skipped while the tab is
 * hidden (unless `criticalWhileHidden`), and becoming visible again polls right away instead of
 * waiting out whatever was left of the paused interval. Returns a `stop` function.
 */
export function startVisibilityAwarePoller(
  callback: () => void | Promise<void>,
  intervalMs: number,
  options: VisibilityAwarePollerOptions = {},
  deps: VisibilityAwarePollerDeps = defaultVisibilityAwarePollerDeps()
): () => void {
  const { criticalWhileHidden = false, runImmediately = false } = options;
  let cancelled = false;
  let isRunning = false;
  let timeoutId: number | null = null;

  const scheduleNext = (delay: number): void => {
    if (cancelled) {
      return;
    }

    timeoutId = deps.setTimeout(runTick, delay);
  };

  function runTick(): void {
    if (cancelled || isRunning) {
      return;
    }

    if (!criticalWhileHidden && deps.isHidden()) {
      scheduleNext(intervalMs);
      return;
    }

    isRunning = true;
    Promise.resolve()
      .then(() => callback())
      .catch(() => {
        // Transient poll errors are the caller's concern (e.g. surfaced via react-query state);
        // this loop just keeps ticking on schedule regardless.
      })
      .finally(() => {
        isRunning = false;
        scheduleNext(intervalMs);
      });
  }

  function handleVisibilityChange(): void {
    if (cancelled || deps.isHidden()) {
      return;
    }

    if (timeoutId !== null) {
      deps.clearTimeout(timeoutId);
      timeoutId = null;
    }

    runTick();
  }

  deps.addVisibilityListener(handleVisibilityChange);
  scheduleNext(runImmediately ? 0 : intervalMs);

  return () => {
    cancelled = true;
    if (timeoutId !== null) {
      deps.clearTimeout(timeoutId);
      timeoutId = null;
    }

    deps.removeVisibilityListener(handleVisibilityChange);
  };
}
